import type { EnemyDef, EnemyTypeId, GameState, WorldRef } from './types';
import { ZONES, bossIdForLevel } from '../data/enemies';
import type { Rng } from './rng';

/**
 * **Mode infini** (phase 5, A78, A82 ; v0.40.0, monde par combat) — fonctions
 * pures, sans DOM ni état caché : monde d'un combat, boss d'une Invasion,
 * bestiaire tirable.
 *
 * Campagne (niveaux 1 à 10) : la zone est le niveau (A25), le boss celui de la
 * zone ; `state.world` et `state.bossId` restent `null`. Au-delà (niveau 11 et
 * plus) : un **monde tiré par combat** (`drawNextWorld`) — une des dix zones,
 * jamais celle du combat précédent, ou un monde **procédural** avec une chance
 * sur quatre, jamais deux de suite — et un **boss tiré par niveau** parmi les
 * dix (`drawBoss`), indépendamment du monde. `state.world` est le monde du
 * **prochain** combat : tiré à la fin du précédent, affiché par l'Accalmie.
 * Les croissances de PV et d'or ne changent pas : `enemyHp` prolonge déjà sa
 * courbe au-delà de la table (`hpLevelGrowth`).
 */

/** Premier niveau du mode infini (11) : juste après la dernière zone. */
export const ENDLESS_FROM = ZONES.length + 1;

/**
 * Probabilité qu'un combat infini se joue dans un monde procédural (A78 : « un
 * sur quatre »), sauf juste après un monde procédural (jamais deux de suite).
 */
export const PROCEDURAL_CHANCE = 1 / 4;

/** Plus grande graine de décor procédural (entier 0 … 2^31 − 1). */
export const WORLD_SEED_MAX = 2 ** 31 - 1;

/**
 * Nom affiché d'un monde procédural (en-tête de l'Accalmie). Choix de
 * l'orchestrateur : le décor mêle les briques de plusieurs zones.
 */
export const PROCEDURAL_WORLD_NAME = 'Terres mêlées';

/** Les dix boss, dans l'ordre des zones. */
export const BOSS_IDS: readonly EnemyTypeId[] = ZONES.map((z) => z.bossId);

/** Le niveau est-il en mode infini (≥ 11) ? */
export function isEndless(level: number): boolean {
  return level >= ENDLESS_FROM;
}

/** `id` est-il l'un des dix boss ? */
export function isBossId(id: unknown): id is EnemyTypeId {
  return typeof id === 'string' && (BOSS_IDS as readonly string[]).includes(id);
}

/**
 * Un `WorldRef` valide : zone entière 1 … 10, ou graine entière
 * 0 … 2^31 − 1. Sert à la relecture des sauvegardes.
 */
export function isValidWorld(v: unknown): v is WorldRef {
  if (typeof v !== 'object' || v === null) return false;
  const w = v as { kind?: unknown; zone?: unknown; seed?: unknown };
  if (w.kind === 'zone') {
    return Number.isInteger(w.zone) && (w.zone as number) >= 1 && (w.zone as number) <= ZONES.length;
  }
  if (w.kind === 'procedural') {
    return Number.isInteger(w.seed) && (w.seed as number) >= 0 && (w.seed as number) <= WORLD_SEED_MAX;
  }
  return false;
}

/** Repli du mode infini quand l'état n'a pas (encore) de monde : la zone 10. */
const FALLBACK_WORLD: WorldRef = { kind: 'zone', zone: ZONES.length };

/**
 * Monde du niveau courant. Campagne : `{ kind: 'zone', zone: level }` ; mode
 * infini : `state.world`, repli zone 10 s'il manque.
 */
export function worldOf(state: Pick<GameState, 'level' | 'world'>): WorldRef {
  if (!isEndless(state.level)) {
    return { kind: 'zone', zone: Math.min(Math.max(1, Math.round(state.level)), ZONES.length) };
  }
  return state.world ?? { ...FALLBACK_WORLD };
}

/**
 * Boss de l'Invasion du niveau courant. Campagne : celui de la zone ; mode
 * infini : `state.bossId`, repli boss de la zone 10 (le Tricorne).
 */
export function currentBossId(state: Pick<GameState, 'level' | 'bossId'>): EnemyTypeId {
  if (!isEndless(state.level)) return bossIdForLevel(state.level);
  return state.bossId ?? ZONES[ZONES.length - 1].bossId;
}

/**
 * Tire le monde du **prochain** combat infini, `previous` étant celui du
 * combat précédent (`null` s'il n'y en a pas, au chargement d'une partie sans
 * monde) :
 *  1. `previous` n'est pas procédural et `rng.chance(1/4)` → monde
 *     **procédural**, graine `rng.int(0, 2^31 − 1)` ;
 *  2. sinon une **zone** à poids égaux parmi les dix, **sauf** celle de
 *     `previous` (`rng.int(1, 9)` remappé pour la sauter) ; sans précédent ou
 *     après un monde procédural : `rng.int(1, 10)`.
 * Au plus deux tirages (`chance` puis `int`), dans cet ordre : reproductible
 * avec un `Rng` de graine fixe. Aucun tirage de boss ici (`drawBoss`).
 */
export function drawNextWorld(previous: WorldRef | null, rng: Rng): WorldRef {
  const afterProcedural = previous?.kind === 'procedural';
  if (!afterProcedural && rng.chance(PROCEDURAL_CHANCE)) {
    return { kind: 'procedural', seed: rng.int(0, WORLD_SEED_MAX) };
  }
  if (previous?.kind === 'zone') {
    const skip = Math.min(Math.max(1, Math.round(previous.zone)), ZONES.length);
    const zone = rng.int(1, ZONES.length - 1);
    return { kind: 'zone', zone: zone >= skip ? zone + 1 : zone };
  }
  return { kind: 'zone', zone: rng.int(1, ZONES.length) };
}

/**
 * Tire le boss d'un niveau infini, à poids égaux parmi les dix, indépendant du
 * monde : un seul tirage `rng.int(0, 9)`.
 */
export function drawBoss(rng: Rng): EnemyTypeId {
  return BOSS_IDS[rng.int(0, BOSS_IDS.length - 1)];
}

/** Nom d'un monde : celui de sa zone, ou « Terres mêlées » pour le procédural. */
export function worldName(world: WorldRef): string {
  if (world.kind === 'procedural') return PROCEDURAL_WORLD_NAME;
  const zone = ZONES[Math.min(Math.max(1, Math.round(world.zone)), ZONES.length) - 1];
  return zone.name;
}

/**
 * Bestiaire tirable d'un monde infini au jour `day` (6 = Invasion), à poids
 * égaux. Monde zone : les ordinaires de la zone avec `minDay ≤ day` ; monde
 * procédural : les ordinaires des dix zones avec `minDay ≤ day`.
 *
 * v0.49.0 (COMMENTS v0.48.1 §1.7) : les **boss sont exclus** — des Expéditions
 * comme des quatre premiers ennemis de l'Invasion ; le boss tiré (`drawBoss`)
 * reste le dernier monstre de l'Invasion infinie. Un monde dont le pool serait
 * vide ce jour-là retombe sur les ordinaires de ses zones sans contrainte de
 * jour (A82 : « bestiaire des 50 » jusqu'à la v0.48).
 */
export function endlessPool(world: WorldRef, day: number): EnemyDef[] {
  const zones =
    world.kind === 'procedural'
      ? ZONES
      : [ZONES[Math.min(Math.max(1, Math.round(world.zone)), ZONES.length) - 1]];
  const ordinary = zones.flatMap((z) => z.enemies.filter((e) => e.boss !== true));
  const pool = ordinary.filter((e) => e.minDay <= day);
  return pool.length > 0 ? pool : ordinary;
}
